import { useState, useCallback } from 'react'
import { httpsCallable } from 'firebase/functions'
import { useToast } from '@chakra-ui/react'
import { SendOTPResponse } from '../../../types/EmailVerification'
import { COOLDOWN_SECONDS } from '../../../constants/emailVerification'
import { getEffectiveServices } from '../../../utils/firebaseServiceContext'
import useStore from '../../../store/useStore'
import { auditBlockedImpersonationAction } from '../../../utils/impersonationAudit'

interface UseResendOTPReturn {
  resendOTP: () => Promise<void>
  loading: boolean
  cooldownSeconds: number
  setCooldownSeconds: (seconds: number) => void
}

/**
 * Hook for handling OTP resend logic on the VerifyEmail page.
 * Manages cooldown state, loading state, and toast notifications.
 */
export function useResendOTP(onSuccess?: () => void): UseResendOTPReturn {
  const [loading, setLoading] = useState(false)
  const [cooldownSeconds, setCooldownSeconds] = useState(COOLDOWN_SECONDS)

  const toast = useToast()
  const isImpersonating = useStore(
    (state) => state.identityStatus === 'impersonating',
  )

  const resendOTP = useCallback(async () => {
    setLoading(true)

    try {
      if (isImpersonating) {
        await auditBlockedImpersonationAction('account.otp-send')
        throw new Error('OTP is disabled while acting as a user.')
      }
      const functions = getEffectiveServices().functions
      const callable = httpsCallable<void, SendOTPResponse>(
        functions,
        'sendOTPCallable'
      )

      const result = await callable()

      if (result.data.success) {
        toast({
          status: 'success',
          title: 'Code Sent',
          description: 'A new verification code has been sent to your email.',
          duration: 5000,
          isClosable: true,
        })
        setCooldownSeconds(COOLDOWN_SECONDS)
        onSuccess?.()
      } else if (result.data.error === 'cooldown') {
        setCooldownSeconds(result.data.cooldownRemaining || COOLDOWN_SECONDS)
        toast({
          status: 'warning',
          title: 'Please Wait',
          description: `You can request a new code in ${result.data.cooldownRemaining} seconds.`,
          duration: 3000,
          isClosable: true,
        })
      } else {
        toast({
          status: 'error',
          title: 'Failed to Send',
          description: 'Could not send verification code. Please try again.',
          duration: 5000,
          isClosable: true,
        })
      }
    } catch (err) {
      console.error('Resend error:', err)
      toast({
        status: 'error',
        title: 'Error',
        description: 'An error occurred. Please try again.',
        duration: 5000,
        isClosable: true,
      })
    } finally {
      setLoading(false)
    }
  }, [toast, onSuccess, isImpersonating])

  return {
    resendOTP,
    loading,
    cooldownSeconds,
    setCooldownSeconds,
  }
}
